import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import useIsMobile from '../hooks/useIsMobile';
import toast from 'react-hot-toast';
import { requiredMessage, FieldError, Req, ERROR_COLOR } from '../utils/formErrors';

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

const EMPTY_FORM = { title: '', body: '', url: '/', target_type: 'church', church_id: '' };

export default function NotificationsPush() {
  const { user } = useAuth();
  const mob = useIsMobile();
  const isSuperAdmin = user?.role === 'superadmin_platform';

  const [form, setForm] = useState({ ...EMPTY_FORM, target_type: isSuperAdmin ? 'all' : 'church' });
  const [churches, setChurches] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (isSuperAdmin) {
      api.get('/churches/').then(r => setChurches(Array.isArray(r.data) ? r.data : r.data.results || [])).catch(() => {});
    }
  }, [isSuperAdmin]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/notifications/broadcast/');
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleDelete = async (item) => {
    if (!window.confirm(`Supprimer la notification "${item.title}" de l'historique ?`)) return;
    try {
      await api.delete(`/notifications/broadcast/${item.id}/`);
      setItems(prev => prev.filter(i => i.id !== item.id));
      toast.success('Notification supprimée.');
    } catch {
      toast.error('Erreur lors de la suppression.');
    }
  };

  const handleChange = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const [fieldErrors, setFieldErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!form.title.trim()) errors.title = requiredMessage('Titre');
    if (isSuperAdmin && form.target_type === 'church' && !form.church_id) errors.church_id = requiredMessage('Église');
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('body', form.body);
      fd.append('url', form.url || '/');
      fd.append('target_type', form.target_type);
      if (isSuperAdmin && form.target_type === 'church') fd.append('church_id', form.church_id);
      if (imageFile) fd.append('image', imageFile);
      const res = await api.post('/notifications/broadcast/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(res.data.message || 'Notification envoyée.');
      setForm(f => ({ ...EMPTY_FORM, target_type: f.target_type }));
      clearImage();
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur lors de l'envoi.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
          Notifications push
        </h1>
        <p style={{ color: 'var(--text-secondary)', marginTop: '4px', fontSize: '0.9rem' }}>
          Envoyer une notification push aux appareils abonnés{isSuperAdmin ? ' (toute la plateforme ou une église)' : ' de votre église'}.
        </p>
      </div>

      <form onSubmit={handleSubmit} noValidate style={{
        background: 'var(--card-bg)', borderRadius: '12px', border: '1px solid var(--border)',
        boxShadow: 'var(--card-shadow)', padding: '20px', marginBottom: '24px', maxWidth: '560px',
      }}>
        <div style={{ marginBottom: '14px' }}>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px', color: 'var(--text-primary)' }}>Titre<Req /></label>
          <input value={form.title} onChange={handleChange('title')} maxLength={150}
            style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${fieldErrors.title ? ERROR_COLOR : 'var(--border)'}`, fontSize: '0.9rem', background: '#fff', color: 'var(--text-primary)' }} />
          <FieldError message={fieldErrors.title} />
        </div>

        <div style={{ marginBottom: '14px' }}>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px', color: 'var(--text-primary)' }}>Message</label>
          <textarea value={form.body} onChange={handleChange('body')} rows={3}
            style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '0.9rem', resize: 'vertical', background: '#fff', color: 'var(--text-primary)' }} />
        </div>

        <div style={{ marginBottom: '14px' }}>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px', color: 'var(--text-primary)' }}>Lien à ouvrir au clic</label>
          <input value={form.url} onChange={handleChange('url')} placeholder="/dashboard"
            style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '0.9rem', background: '#fff', color: 'var(--text-primary)' }} />
        </div>

        <div style={{ marginBottom: '14px' }}>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px', color: 'var(--text-primary)' }}>
            Image / affiche <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(optionnel — visible sur Android, ignorée sur iOS)</span>
          </label>
          {imagePreview ? (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <img src={imagePreview} alt="aperçu" style={{ maxWidth: '220px', maxHeight: '140px', borderRadius: '8px', border: '1px solid var(--border)', display: 'block' }} />
              <button type="button" onClick={clearImage}
                style={{ position: 'absolute', top: '-8px', right: '-8px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem' }}>
                ×
              </button>
            </div>
          ) : (
            <input type="file" accept="image/*" onChange={handleImageChange}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '0.85rem', background: '#fff', color: 'var(--text-primary)' }} />
          )}
        </div>

        {isSuperAdmin && (
          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px', color: 'var(--text-primary)' }}>Destinataires</label>
            <select value={form.target_type} onChange={handleChange('target_type')}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '0.9rem', background: '#fff', color: 'var(--text-primary)' }}>
              <option value="all">Toute la plateforme</option>
              <option value="church">Une église spécifique</option>
            </select>
          </div>
        )}

        {isSuperAdmin && form.target_type === 'church' && (
          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px', color: 'var(--text-primary)' }}>Église<Req /></label>
            <select value={form.church_id} onChange={handleChange('church_id')}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${fieldErrors.church_id ? ERROR_COLOR : 'var(--border)'}`, fontSize: '0.9rem', background: '#fff', color: 'var(--text-primary)' }}>
              <option value="">— Choisir une église —</option>
              {churches.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <FieldError message={fieldErrors.church_id} />
          </div>
        )}

        <button type="submit" disabled={sending}
          style={{
            background: '#22c55e', color: '#fff', border: 'none', borderRadius: 8,
            padding: '10px 22px', fontWeight: 700, fontSize: '0.9rem', cursor: sending ? 'default' : 'pointer',
            opacity: sending ? 0.7 : 1,
          }}>
          {sending ? 'Envoi...' : 'Envoyer'}
        </button>
      </form>

      <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px' }}>Historique</h2>
      <div style={{ marginBottom: '12px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
        {loading ? 'Chargement...' : `${items.length} notification${items.length !== 1 ? 's' : ''} envoyée${items.length !== 1 ? 's' : ''}`}
      </div>

      <div style={{ background: 'var(--card-bg)', borderRadius: '12px', border: '1px solid var(--border)', overflowX: 'auto', WebkitOverflowScrolling: 'touch', boxShadow: 'var(--card-shadow)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: mob ? '600px' : 'auto' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '2px solid var(--border)' }}>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Titre</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cible</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Envoyé par</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Destinataires</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Chargement...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Aucune notification envoyée</td></tr>
            ) : (
              items.map((item, i) => (
                <tr key={item.id} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'rgba(248,250,252,0.5)' }}>
                  <td style={{ padding: '10px 16px', fontSize: '0.82rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{formatDate(item.created_at)}</td>
                  <td style={{ padding: '10px 16px', fontSize: '0.87rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {item.image_url && (
                        <img src={item.image_url} alt="" style={{ width: '40px', height: '40px', borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }} />
                      )}
                      <div>
                        {item.title}
                        {item.body && <div style={{ fontWeight: 400, color: 'var(--text-secondary)', marginTop: '2px' }}>{item.body}</div>}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '10px 16px', fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                    {item.target_type === 'all' ? 'Toute la plateforme' : (item.church || '—')}
                  </td>
                  <td style={{ padding: '10px 16px', fontSize: '0.85rem', color: 'var(--text-primary)' }}>{item.sent_by || '—'}</td>
                  <td style={{ padding: '10px 16px', fontSize: '0.85rem', color: 'var(--text-primary)' }}>{item.recipient_count}</td>
                  <td style={{ padding: '10px 16px' }}>
                    <button onClick={() => handleDelete(item)}
                      style={{ background: '#fef2f2', color: '#b91c1c', border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}>
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
